import _ from 'lodash';
import { type JSX, useCallback, useMemo, useState } from 'react';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { Button } from 'Components/Buttons/Buttons';
import { submitApiRequestWithNewFiles } from 'Components/Files/FileManagementArea';
import { Modal } from 'Components/Modal/Modal';
import { PageLayoutQuestionnaire, SidebarSection as PageSidebarSection } from 'Components/PageLayout/PageLayoutQuestionnaire/PageLayoutQuestionnaire';
import { RequiresAttentionMarker } from 'Components/RequiresAttentionMarker/RequiresAttentionMarker';
import { Text } from 'Components/Text/Text';
import { ControlQuestionsView } from 'Components/UcrmTprm/QuestionnaireForm/ControlQuestionsView/ControlQuestionsView';
import { useQuestionnaireStatistics } from 'Components/UcrmTprm/QuestionnaireForm/Hooks/useQuestionnaireStatistics';
import { QuestionnaireReviewSection } from 'Components/UcrmTprm/QuestionnaireForm/QuestionnaireReviewSection/QuestionnaireReviewSection';
import { ICON_CLOSE, ICON_SAVE } from 'Config/Icons';
import { DueDiligenceAnswer, DueDiligenceAnswerDate, DueDiligenceAnswerFileUpload, DueDiligenceAnswerFreeformText, DueDiligenceAnswerMultipleSelect, DueDiligenceAnswerSingleSelect, DueDiligenceQuestionType, QuestionnaireContent, isQuestionAnswered } from 'Models/DueDiligence';
import { FileToBeUploaded, UploadedFile } from 'Models/Files';

import styles from './QuestionnaireForm.module.css';

/**
 * @param getLeadingSidebarSections Builds the sidebar sections displayed before the questionnaire's control group sections, such as status and discussion. Call `onQuestionnaireChange` to replace the questionnaire held by this form, e.g., after a discussion message is sent.
 * @param isSubmitted Whether the given questionnaire has been submitted, since the two questionnaire models record submission differently. A submitted questionnaire cannot be answered.
 * @param notifiedParty Who is notified when the questionnaire is submitted. Used mid-sentence.
 * @param saveControlGroup Sends the answers for one control group (section), including any newly selected files, to the backend and resolves to the updated questionnaire.
 * @param submitQuestionnaire Submits the questionnaire and resolves to the updated questionnaire. Errors are not caught, because this is called from within a `ConfirmationModal`, which displays them.
 * @param cannotRespondReason If provided, the user can view the questionnaire but cannot answer or submit it, and this explanation is displayed in the Submit section (where it is passed to `QuestionnaireReviewSection` as `cannotSubmitReason`).
 */
export interface QuestionnaireFormProps<T extends QuestionnaireContent> {
    documentApi: DocumentApi;
    getLeadingSidebarSections: (questionnaire: T, onQuestionnaireChange: (updatedQuestionnaire: T) => void) => PageSidebarSection[];
    headerBreadcrumb: JSX.Element;
    headerDescription: string;
    headerTitle: string;
    initialQuestionnaire: T;
    isSubmitted: (questionnaire: T) => boolean;
    notifiedParty: string;
    saveControlGroup: (questionnaire: T, frameworkId: string, groupId: string, answers: DueDiligenceAnswer[]) => Promise<T>;
    submitQuestionnaire: (questionnaire: T) => Promise<T>;

    cannotRespondReason?: string;
}

/**
 * Renders a questionnaire for a user to answer, one control group (section) at a time, followed by a section where the questionnaire can be submitted.
 * Used by the TPRM questionnaire page (where third-party users answer questions) and the UCRM questionnaire page (where SummIT users answer questions).
 * The two pages represent questionnaires with different models, so this component is generic over the questionnaire type; it only reads and modifies `control_frameworks`.
 *
 * Gotcha: answers are saved per section. Navigating away from a section with unsaved changes prompts the user to save or discard them.
 */
export const QuestionnaireForm = <T extends QuestionnaireContent>(props: QuestionnaireFormProps<T>): JSX.Element => {
    const [questionnaire, setQuestionnaire] = useState<T>(props.initialQuestionnaire);
    const [lastSavedQuestionnaire, setLastSavedQuestionnaire] = useState<T>(props.initialQuestionnaire);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<{
        fromIndex: number;
        toIndex: number;
        resolve: (allowNavigation: boolean) => void;
    }>();

    const [allPendingFiles, setAllPendingFiles] = useState<File[]>([]);
    const [selectedPendingFilesByQuestion, setSelectedPendingFilesByQuestion] = useState<Map<string, File[]>>(new Map());
    /** Tracks a failure when saving a section, keyed by the section's group identifier so the error is shown in that section's footer. */
    const [groupSaveError, setGroupSaveError] = useState<{ groupIdentifier: string; message: string }>();

    const overallStatistics = useQuestionnaireStatistics(questionnaire);
    const isSubmitted = props.isSubmitted(questionnaire);
    const answersAreDisabled = isSubmitted || props.cannotRespondReason !== undefined;

    const handleSaveQuestionFileChanges = useCallback((questionId: string, newPendingFiles: File[], selectedPendingFiles: File[], selectedUploadedFiles: UploadedFile[]) => {
        setAllPendingFiles((current) => [...current, ...newPendingFiles]);

        setSelectedPendingFilesByQuestion((current) => {
            const updated = new Map(current);
            updated.set(questionId, selectedPendingFiles);
            return updated;
        });

        setQuestionnaire((currentQuestionnaire) => {
            const updatedQuestionnaire = _.cloneDeep(currentQuestionnaire);
            for (const framework of updatedQuestionnaire.control_frameworks) {
                for (const group of framework.control_groups) {
                    for (const control of group.controls) {
                        for (const question of control.questions) {
                            if (question.id === questionId) {
                                question.files = selectedUploadedFiles;
                                // Files are the answer to a File upload question, so providing one means the user is not unable to answer it. Files on any other question type are merely supporting documentation, so they have no bearing on `is_unable_to_answer`.
                                if (question._type === DueDiligenceQuestionType.FILE_UPLOAD && (selectedUploadedFiles.length > 0 || selectedPendingFiles.length > 0)) {
                                    question.is_unable_to_answer = false;
                                }
                                return updatedQuestionnaire;
                            }
                        }
                    }
                }
            }
            return updatedQuestionnaire;
        });
    }, []);

    /**
     * Records that the user is unable to answer a required Date or File upload question.
     * This is handled at the form level rather than in `ControlQuestionsView` because checking the box must also clear the question's pending files, which are tracked here and not on the question itself.
     */
    const handleUnableToAnswerChange = useCallback(
        (questionId: string, isUnableToAnswer: boolean) => {
            if (isUnableToAnswer && (selectedPendingFilesByQuestion.get(questionId) ?? []).length > 0) {
                const remainingSelections = new Map(selectedPendingFilesByQuestion);
                remainingSelections.delete(questionId);
                setSelectedPendingFilesByQuestion(remainingSelections);

                // A pending file can be selected by more than one question, so only forget the ones that no question references anymore.
                const stillSelectedFiles = new Set([...remainingSelections.values()].flat());
                setAllPendingFiles((current) => current.filter((file) => stillSelectedFiles.has(file)));
            }

            setQuestionnaire((currentQuestionnaire) => {
                const updatedQuestionnaire = _.cloneDeep(currentQuestionnaire);
                for (const framework of updatedQuestionnaire.control_frameworks) {
                    for (const group of framework.control_groups) {
                        for (const control of group.controls) {
                            for (const question of control.questions) {
                                if (question.id === questionId && (question._type === DueDiligenceQuestionType.DATE || question._type === DueDiligenceQuestionType.FILE_UPLOAD)) {
                                    question.is_unable_to_answer = isUnableToAnswer;

                                    // The flag and the answer are mutually exclusive, so checking the box discards whatever answer was already given.
                                    if (isUnableToAnswer) {
                                        question.files = [];
                                        if (question._type === DueDiligenceQuestionType.DATE) {
                                            question.answer = undefined;
                                        }
                                    }
                                    return updatedQuestionnaire;
                                }
                            }
                        }
                    }
                }
                return updatedQuestionnaire;
            });
        },
        [selectedPendingFilesByQuestion]
    );

    const handleSaveControlGroup = useCallback(
        async (frameworkIdentifier: string, groupIdentifier: string) => {
            setIsSubmitting(true);

            try {
                const framework = questionnaire.control_frameworks.find((f) => f.identifier === frameworkIdentifier);
                const group = framework!.control_groups.find((g) => g.identifier === groupIdentifier);
                const [frameworkId, groupId] = groupIdentifier.split('#');

                const submitApiRequest = async (getFilesToBeUploaded: (questionId: string) => FileToBeUploaded[]): Promise<T> => {
                    const answers: DueDiligenceAnswer[] = [];

                    group!.controls.forEach((control) => {
                        control.questions.forEach((question) => {
                            const filesToUpload = getFilesToBeUploaded(question.id);

                            let answer: DueDiligenceAnswer;

                            switch (question._type) {
                                case DueDiligenceQuestionType.SINGLE_SELECT:
                                    answer = {
                                        _type: DueDiligenceQuestionType.SINGLE_SELECT,
                                        question_id: question.id,
                                        selection: question.answer,
                                        comments: question.comments,
                                        new_files: filesToUpload,
                                        selected_existing_file_ids: question.files.map((f) => f.file_id),
                                    } as DueDiligenceAnswerSingleSelect;
                                    break;

                                case DueDiligenceQuestionType.MULTIPLE_SELECT:
                                    answer = {
                                        _type: DueDiligenceQuestionType.MULTIPLE_SELECT,
                                        question_id: question.id,
                                        selections: question.answer,
                                        comments: question.comments,
                                        new_files: filesToUpload,
                                        selected_existing_file_ids: question.files.map((f) => f.file_id),
                                    } as DueDiligenceAnswerMultipleSelect;
                                    break;

                                case DueDiligenceQuestionType.DATE:
                                    answer = {
                                        _type: DueDiligenceQuestionType.DATE,
                                        question_id: question.id,
                                        date: question.answer,
                                        is_unable_to_answer: question.is_unable_to_answer,
                                        comments: question.comments,
                                        new_files: filesToUpload,
                                        selected_existing_file_ids: question.files.map((f) => f.file_id),
                                    } as DueDiligenceAnswerDate;
                                    break;

                                case DueDiligenceQuestionType.FILE_UPLOAD:
                                    answer = {
                                        _type: DueDiligenceQuestionType.FILE_UPLOAD,
                                        question_id: question.id,
                                        is_unable_to_answer: question.is_unable_to_answer,
                                        comments: question.comments,
                                        new_files: filesToUpload,
                                        selected_existing_file_ids: question.files.map((f) => f.file_id),
                                    } as DueDiligenceAnswerFileUpload;
                                    break;

                                case DueDiligenceQuestionType.FREEFORM_TEXT:
                                    answer = {
                                        _type: DueDiligenceQuestionType.FREEFORM_TEXT,
                                        question_id: question.id,
                                        comments: question.comments,
                                        new_files: filesToUpload,
                                        selected_existing_file_ids: question.files.map((f) => f.file_id),
                                    } as DueDiligenceAnswerFreeformText;
                                    break;

                                default:
                                    return;
                            }

                            answers.push(answer);
                        });
                    });

                    return props.saveControlGroup(questionnaire, frameworkId, groupId, answers);
                };

                const updatedQuestionnaire = await submitApiRequestWithNewFiles(props.documentApi, selectedPendingFilesByQuestion, submitApiRequest);

                setQuestionnaire(updatedQuestionnaire);
                setLastSavedQuestionnaire(updatedQuestionnaire);
                setAllPendingFiles([]);
                setSelectedPendingFilesByQuestion(new Map());
                setGroupSaveError(undefined);
            } catch (error) {
                setGroupSaveError({ groupIdentifier, message: error.message });
            } finally {
                setIsSubmitting(false);
            }
        },
        [questionnaire, props, selectedPendingFilesByQuestion]
    );

    const performSubmit = useCallback(async (): Promise<void> => {
        // Does not try-catch because this is used within a ConfirmationModal.
        const updatedQuestionnaire = await props.submitQuestionnaire(questionnaire);

        setQuestionnaire(updatedQuestionnaire);
        setLastSavedQuestionnaire(updatedQuestionnaire);
    }, [questionnaire, props]);

    const sidebarSections: PageSidebarSection[] = useMemo(() => {
        const sections: PageSidebarSection[] = [...props.getLeadingSidebarSections(questionnaire, setQuestionnaire)];

        questionnaire.control_frameworks.forEach((framework) => {
            framework.control_groups.forEach((group) => {
                const lastSavedFramework = lastSavedQuestionnaire.control_frameworks.find((f) => f.identifier === framework.identifier);
                const lastSavedGroup = lastSavedFramework?.control_groups.find((g) => g.identifier === group.identifier);

                let unansweredCount = 0;
                let requiredUnansweredCount = 0;
                if (lastSavedGroup) {
                    lastSavedGroup.controls.forEach((control) => {
                        control.questions.forEach((question) => {
                            if (!isQuestionAnswered(question)) {
                                unansweredCount++;

                                if (question.is_required) {
                                    requiredUnansweredCount++;
                                }
                            }
                        });
                    });
                }

                // Important: If more fields are added to questions in the future, they must be accounted for here.
                const normalizedGroup = normalizeObjectsForComparison(group);
                const normalizedLastSavedGroup = normalizeObjectsForComparison(lastSavedGroup);
                const hasUnsavedNewFiles = allPendingFiles.length > 0;
                const hasUnsavedChanges = !_.isEqual(normalizedGroup, normalizedLastSavedGroup) || hasUnsavedNewFiles;

                sections.push({
                    title: group.name,
                    footerProps: answersAreDisabled
                        ? undefined
                        : {
                              buttons: (
                                  <Button variant="primary" loadingText="Saving..." isLoading={isSubmitting} onClick={() => handleSaveControlGroup(framework.identifier, group.identifier)} fontAwesomeImage={ICON_SAVE}>
                                      Save Section
                                  </Button>
                              ),
                              content: (
                                  <Text variant="Text2" noStyles>
                                      {group.name}
                                  </Text>
                              ),
                              hasUnsavedChanges,
                              errorMessage: groupSaveError?.groupIdentifier === group.identifier ? groupSaveError.message : undefined,
                          },
                    metadata: {
                        frameworkIdentifier: framework.identifier,
                        groupIdentifier: group.identifier,
                    },
                    content: [
                        {
                            content: (
                                <div className={styles.controlsContainer}>
                                    {group.controls.map((control, index) => (
                                        <ControlQuestionsView
                                            key={control.identifier}
                                            control={control}
                                            controlNumber={index + 1}
                                            documentApi={props.documentApi}
                                            existingPendingFiles={allPendingFiles}
                                            lastSavedQuestionnaire={lastSavedQuestionnaire}
                                            selectedPendingFilesByQuestion={selectedPendingFilesByQuestion}
                                            onControlChange={(updatedControl) => {
                                                const updatedQuestionnaire = _.cloneDeep(questionnaire);
                                                const frameworkIndex = updatedQuestionnaire.control_frameworks.findIndex((f) => f.identifier === framework.identifier);
                                                const groupIndex = updatedQuestionnaire.control_frameworks[frameworkIndex].control_groups.findIndex((g) => g.identifier === group.identifier);
                                                const controlIndex = updatedQuestionnaire.control_frameworks[frameworkIndex].control_groups[groupIndex].controls.findIndex((c) => c.identifier === control.identifier);
                                                updatedQuestionnaire.control_frameworks[frameworkIndex].control_groups[groupIndex].controls[controlIndex] = updatedControl;
                                                setQuestionnaire(updatedQuestionnaire);
                                            }}
                                            onSaveQuestionFileChanges={handleSaveQuestionFileChanges}
                                            onUnableToAnswerChange={handleUnableToAnswerChange}
                                            disabled={answersAreDisabled}
                                        />
                                    ))}
                                </div>
                            ),
                        },
                    ],
                    additionalContent: (
                        <>
                            {unansweredCount > 0 && (
                                <RequiresAttentionMarker type="warning" size="sm">
                                    {`${unansweredCount} unanswered question${unansweredCount === 1 ? '' : 's'}`}
                                </RequiresAttentionMarker>
                            )}
                            {requiredUnansweredCount > 0 && (
                                <RequiresAttentionMarker type="negative" size="sm">
                                    {`${requiredUnansweredCount} required`}
                                </RequiresAttentionMarker>
                            )}
                        </>
                    ),
                });
            });
        });

        sections.push({
            title: 'Submit',
            content: [
                {
                    content: <QuestionnaireReviewSection cannotSubmitReason={props.cannotRespondReason} isSubmitted={isSubmitted} notifiedParty={props.notifiedParty} statistics={overallStatistics} onSubmit={performSubmit} />,
                },
            ],
            additionalContent: !isSubmitted ? (
                <RequiresAttentionMarker type="warning" size="sm">
                    Not submitted
                </RequiresAttentionMarker>
            ) : undefined,
        });

        return sections;
    }, [props, questionnaire, isSubmitted, answersAreDisabled, overallStatistics, performSubmit, lastSavedQuestionnaire, allPendingFiles, isSubmitting, handleSaveControlGroup, selectedPendingFilesByQuestion, handleSaveQuestionFileChanges, handleUnableToAnswerChange, groupSaveError]);

    const handleBeforeSectionChange = useCallback(
        (fromIndex: number, toIndex: number): Promise<boolean> => {
            const leavingSection = sidebarSections[fromIndex];

            if (leavingSection.footerProps?.hasUnsavedChanges) {
                return new Promise((resolve) => {
                    setPendingNavigation({ fromIndex, toIndex, resolve });
                });
            }

            return Promise.resolve(true);
        },
        [sidebarSections]
    );

    const handleDiscardChanges = useCallback(() => {
        if (!pendingNavigation) {
            return;
        }

        setQuestionnaire(lastSavedQuestionnaire);
        setAllPendingFiles([]);
        setSelectedPendingFilesByQuestion(new Map());
        pendingNavigation.resolve(true);
        setPendingNavigation(undefined);
    }, [lastSavedQuestionnaire, pendingNavigation]);

    const handleCancelNavigation = useCallback(() => {
        if (!pendingNavigation) {
            return;
        }

        pendingNavigation.resolve(false);
        setPendingNavigation(undefined);
    }, [pendingNavigation]);

    const handleSaveAndContinue = useCallback(async () => {
        if (!pendingNavigation) {
            return;
        }

        const currentSection = sidebarSections[pendingNavigation.fromIndex];
        const metadata = currentSection.metadata as { frameworkIdentifier: string; groupIdentifier: string } | undefined;

        if (metadata) {
            await handleSaveControlGroup(metadata.frameworkIdentifier, metadata.groupIdentifier);
        }

        pendingNavigation.resolve(true);
        setPendingNavigation(undefined);
    }, [pendingNavigation, handleSaveControlGroup, sidebarSections]);

    return (
        <>
            {pendingNavigation && (
                <Modal open header="Unsaved Changes">
                    <Text>There are unsaved changes that will be lost permanently if you navigate away.</Text>
                    <div className={styles.unsavedChangesNavigateChoiceButtons}>
                        <Button variant="danger" onClick={handleDiscardChanges} fontAwesomeImage={ICON_CLOSE}>
                            Discard changes
                        </Button>
                        <div className={styles.unsavedChangesNavigateChoiceButtonsRight}>
                            <Button variant="secondary" onClick={handleCancelNavigation} fontAwesomeImage={ICON_CLOSE}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={handleSaveAndContinue} fontAwesomeImage={ICON_SAVE} isLoading={isSubmitting} loadingText="Saving...">
                                Save changes
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
            <PageLayoutQuestionnaire headerBreadcrumb={props.headerBreadcrumb} headerTitle={props.headerTitle} headerDescription={props.headerDescription} headerDashlets={[]} onBeforeSectionChange={handleBeforeSectionChange} sidebarSections={sidebarSections} sidebarTitle="Sections" />
        </>
    );
};

/**
 * Recursively removes properties with undefined values or empty strings from an object.
 * This ensures that `{ foo: undefined }` and `{ foo: "" }` are both treated the same as `{}` for the purpose of comparing two objects.
 */
const normalizeObjectsForComparison = <T,>(obj: T): T => {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(normalizeObjectsForComparison) as unknown as T;
    }

    const result: any = {};
    for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined && value !== '') {
            result[key] = normalizeObjectsForComparison(value);
        }
    }

    return result as T;
};
